import { Link, useNavigate } from "react-router-dom";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import APIs from "../../Constants/apis";
import axiosInstance from "../../config/axiosApi/axiosApi";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import swal from "sweetalert";
import { floorLoyaltyPoints } from "../../utils/loyaltyPoints";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";



const CUSTOMER_REPORT_SORT_FIELDS = {
  name: "Cust_Fname",
  totalOrders: "Cust_TotalOrderCount",
  totalSpent: "Cust_TotalOrderSpent",
  averageOrderSpent: "Cust_AverageOrderSpent",
  loyaltyPoints: "Cust_LoyaltyPoints",
  totalEarnedLoyaltyPoints: "Cust_TotalEarnedLoyaltyPoints",
  totalSpentLoyaltyPoints: "Cust_TotalSpentLoyaltyPoints",
};

const CUSTOMER_REPORT_SORT_LABELS = {
  [CUSTOMER_REPORT_SORT_FIELDS.name]: "Name",
  [CUSTOMER_REPORT_SORT_FIELDS.totalOrders]: "Total Orders",
  [CUSTOMER_REPORT_SORT_FIELDS.totalSpent]: "Total Spent",
  [CUSTOMER_REPORT_SORT_FIELDS.averageOrderSpent]: "Average Order Spent",
  [CUSTOMER_REPORT_SORT_FIELDS.loyaltyPoints]: "Loyalty Points",
  [CUSTOMER_REPORT_SORT_FIELDS.totalEarnedLoyaltyPoints]: "Total Earned Points",
  [CUSTOMER_REPORT_SORT_FIELDS.totalSpentLoyaltyPoints]: "Total Spent Points",
};

const CustomerReport = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const [compCurrency, setCompCurrency] = useState("GBP");
  const pageKey = "customerReport";
  const defaultVisibleColumns = {
    totalOrders: true,
    totalSpent: true,
    averageOrderSpent: true,
    loyaltyPoints: true,
    totalEarnedLoyaltyPoints: true,
    totalSpentLoyaltyPoints: true,
    lastOrderDate: true,
    currentBalance: true,
  };

  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "Cust_Id", label: "ID" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const SHOW_BULK_ACTIONS = false;

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [pageNumber, setPageNumber] = useState(1);
  const lastSelectedIndex = useRef(null);
  const customersFetchSeqRef = useRef(0);
  const navigate = useNavigate();

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: CUSTOMER_REPORT_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    PKR: "₨",
  };

  const getCurrencySymbol = (currencyCode) => {
    const upper = (currencyCode || "").toString().trim().toUpperCase();
    return currencySymbols[upper] || upper || "£";
  };

  const fetchCompCurrency = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
      const profileData = response.data?.data || response.data;
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      showErrorToast("Failed to load company currency");
    }
  }, []);

  const DeleteCustomer = async (customer) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${customer.cust_Fname + " " + customer.cust_Lname}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    setLoading(true);
    try {
      await axiosInstance
        .delete(APIs.DELETE_CUSTOMER_BY_ID, {
          params: {
            CompID,
            Cust_ID: customer.cust_Id,
          },
        })
        .then((response) => {
          if (response.data.status) {
            setCustomers((prevGroups) =>
              prevGroups.filter((delGroup) => delGroup.cust_Id !== customer.cust_Id)
            );
            showSuccessToast("Customer is Deleted.");
          } else showErrorToast(response.data.message);
        });
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong.");
      setLoading(false);
    }
  };

  const handleSelectItem = (customerId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (pagedCustomers[i]?.cust_Id) {
            newSelected.add(pagedCustomers[i].cust_Id);
          }
        }
      } else {
        if (newSelected.has(customerId)) {
          newSelected.delete(customerId);
        } else {
          newSelected.add(customerId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(pagedCustomers.map((item) => item.cust_Id));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No customers selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected customers?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((customerId) =>
          axiosInstance.delete(APIs.DELETE_CUSTOMER_BY_ID, {
            params: {
              CompID,
              Cust_ID: customerId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchCustomers();
      showSuccessToast(`${selectedItems.size} customers deleted successfully.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected customers.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No customers selected for editing.");
      return;
    }

    const selectedCustomerIds = Array.from(selectedItems);
    navigate(`/customer/bulkEdit`, { state: { selectedCustomerIds } });
  };

  const fetchCustomers = useCallback(async () => {
    const fetchSeq = ++customersFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_CUSTOMERS, {
        params: {
          CompID,
          Search: search?.trim() || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
        },
      });

      if (fetchSeq !== customersFetchSeqRef.current) return;

      const customersData = response.data.data || response.data;

      if (Array.isArray(customersData)) {
        setCustomers(customersData);
        if (!search.trim()) {
          applyCatalogTotalFromFetch(setCatalogTotalRecords, customersData.length);
        }
      } else {
        setCustomers([]);
        if (!search.trim()) {
          applyCatalogTotalFromFetch(setCatalogTotalRecords, 0);
        }
      }
    } catch (error) {
      if (fetchSeq !== customersFetchSeqRef.current) return;
      setCustomers([]);
      showErrorToast("Failed to fetch customers.");
    } finally {
      if (fetchSeq === customersFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, search, sortBy, sortOrder]);

  const totalRecords = customers.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedCustomers = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return customers.slice(start, start + size);
  }, [customers, pageNumber, pageSize]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(customersFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchCustomers(), [fetchCustomers]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  useEffect(() => {
    fetchCompCurrency();
  }, [fetchCompCurrency]);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      const mergedColumns = mergeVisibleColumns(
        preferences.visibleColumns,
        defaultVisibleColumns
      );
      dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  const GoToEdit = (cust_Id) => navigate(`/customer/updateCustomer/${cust_Id}`);

  const hasFiltersApplied = () => search.trim() !== "";
  const isUnfilteredView = !hasFiltersApplied();

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    isUnfilteredView &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No customers match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no customer records to display right now.";
  };

  const renderNoDataMessage = () => {
    if (loading || customers.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No customers found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Customer Report</li>
          </ol>
          <h1 className="page-header mb-0">Customer Report</h1>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <GridEmptyState
            isDarkMode={isDarkMode}
            themeColor={themeColor}
            title="No customers found"
            description={getEmptyStateDescription()}
          />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Customer Report"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name, email..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              {SHOW_BULK_ACTIONS && selectedItems.size > 0 && (
                <div className="mb-3">
                  <div
                    className="p-3 bg-light rounded d-flex align-items-center justify-content-between"
                    style={{ border: "1px solid #dee2e6" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedItems.size} customer{selectedItems.size > 1 ? "s" : ""} selected
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleBulkEdit}
                        title="Edit Selected Customers"
                      >
                        <i className="fa fa-edit me-1" />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={handleBulkDelete}
                        title="Delete Selected Customers"
                      >
                        <i className="fa fa-trash me-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {customers.length > 0 && (
                  <div
                    className="table-responsive mt-3"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {SHOW_BULK_ACTIONS && (
                            <th className="pt-2 pb-2">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  pagedCustomers.length > 0 &&
                                  selectedItems.size === pagedCustomers.length
                                }
                              />
                            </th>
                          )}
                          <SortableColumnHeader
                            sortField={CUSTOMER_REPORT_SORT_FIELDS.name}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleColumnSort}
                          >
                            Customer Name
                          </SortableColumnHeader>
                          {visibleColumns.totalOrders && (
                            <SortableColumnHeader
                              sortField={CUSTOMER_REPORT_SORT_FIELDS.totalOrders}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Total Orders
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.currentBalance && (
                            <th className="pt-2 pb-2 text-center">{`Current Balance (${getCurrencySymbol(compCurrency)})`}</th>
                          )}
                          {visibleColumns.totalSpent && (
                            <SortableColumnHeader
                              sortField={CUSTOMER_REPORT_SORT_FIELDS.totalSpent}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              {`Total Spent (${getCurrencySymbol(compCurrency)})`}
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.averageOrderSpent && (
                            <SortableColumnHeader
                              sortField={CUSTOMER_REPORT_SORT_FIELDS.averageOrderSpent}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              {`Avg Order Spent (${getCurrencySymbol(compCurrency)})`}
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.loyaltyPoints && (
                            <SortableColumnHeader
                              sortField={CUSTOMER_REPORT_SORT_FIELDS.loyaltyPoints}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Loyalty Points
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.totalEarnedLoyaltyPoints && (
                            <SortableColumnHeader
                              sortField={CUSTOMER_REPORT_SORT_FIELDS.totalEarnedLoyaltyPoints}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Total Earned Points
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.totalSpentLoyaltyPoints && (
                            <SortableColumnHeader
                              sortField={CUSTOMER_REPORT_SORT_FIELDS.totalSpentLoyaltyPoints}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Total Spent Points
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.lastOrderDate && (
                            <th className="pt-2 pb-2 text-center">Last Order Date</th>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {pagedCustomers.map((customer, index) => (
                          <tr key={customer.cust_Id}>
                            {SHOW_BULK_ACTIONS && (
                              <td className="align-middle">
                                <input
                                  type="checkbox"
                                  checked={selectedItems.has(customer.cust_Id)}
                                  onChange={(e) =>
                                    handleSelectItem(customer.cust_Id, index, e)
                                  }
                                  data-chkbox-shiftsel="type1"
                                />
                              </td>
                            )}
                            <td
                              className="align-middle"
                              onClick={() => {
                                GoToEdit(customer.cust_Id);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_Fname + " " + customer.cust_Lname}
                            </td>
                            {visibleColumns.totalOrders && (
                              <td
                                className="align-middle text-center"
                                onClick={() => {
                                  GoToEdit(customer.cust_Id);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {customer.cust_TotalOrderCount || 0}
                              </td>
                            )}
                            {visibleColumns.currentBalance && (
                              <td
                                className="align-middle text-center"
                                onClick={() => {
                                  GoToEdit(customer.cust_Id);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {(parseFloat(customer.cust_CurrentBalance || 0) || 0).toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.totalSpent && (
                              <td
                                className="align-middle text-center"
                                onClick={() => {
                                  GoToEdit(customer.cust_Id);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {(parseFloat(customer.cust_TotalOrderSpent || 0) || 0).toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.averageOrderSpent && (
                              <td
                                className="align-middle text-center"
                                onClick={() => {
                                  GoToEdit(customer.cust_Id);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {(parseFloat(customer.cust_AverageOrderSpent || 0) || 0).toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.loyaltyPoints && (
                              <td
                                className="align-middle text-center"
                                onClick={() => {
                                  GoToEdit(customer.cust_Id);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {floorLoyaltyPoints(customer.cust_LoyaltyPoints)}
                              </td>
                            )}
                            {visibleColumns.totalEarnedLoyaltyPoints && (
                              <td
                                className="align-middle text-center"
                                onClick={() => {
                                  GoToEdit(customer.cust_Id);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <span className="text-success fw-semibold">
                                  {floorLoyaltyPoints(customer.cust_TotalEarnedLoyaltyPoints)}
                                </span>
                              </td>
                            )}
                            {visibleColumns.totalSpentLoyaltyPoints && (
                              <td
                                className="align-middle text-center"
                                onClick={() => {
                                  GoToEdit(customer.cust_Id);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <span className="text-danger fw-semibold">
                                  {floorLoyaltyPoints(customer.cust_TotalSpentLoyaltyPoints)}
                                </span>
                              </td>
                            )}
                            {visibleColumns.lastOrderDate && (
                              <td
                                className="align-middle text-center"
                                onClick={() => {
                                  GoToEdit(customer.cust_Id);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {customer.cus_LastOrderDate
                                  ? new Date(customer.cus_LastOrderDate).toLocaleDateString()
                                  : "Never"}
                              </td>
                            )}
                            <td className="align-middle text-center">
                              <TableActionsMenu menuStyle={{ zIndex: 1050 }}>
                                <TableActionsItem
                                  as={Link}
                                  to={`/customer/updateCustomer/${customer.cust_Id}`}
                                  icon="fa fa-edit"
                                >
                                  Edit
                                </TableActionsItem>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() => DeleteCustomer(customer)}
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>
    </div>
  );
};

export default CustomerReport;
